/**
 * @fileoverview Shared React hook for import-template authoring state and navigation.
 * @module import-editor/useTemplateEditorState
 *
 * Responsibilities:
 * - Own local wizard state for the shared import-template editor
 * - Manage tab navigation, autosave triggers, and draft dirty tracking
 * - Track visited and completed steps for accurate draft resume behaviour
 *
 * Dependencies:
 * - React state/effect hooks
 * - `../templateEditor/types` for editor contracts
 * - `../utils/templateValidation` for built-in tab validation
 *
 * Architecture:
 * - Pure local UI state machine with host-supplied autosave callback
 * - No direct API or Dexie access from the hook itself
 * - Host-injected tabs participate in navigation while persistence remains local-first
 *
 * Notes:
 * - Completed tabs represent steps the user successfully advanced past, not merely visited panels
 */

import {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { FieldMapping, ImportDataMode } from '@firesquirrel/local-db';
import type {
  TemplateEditorState,
  TemplateEditorTab,
  BladeState,
  EditorTabDescriptor,
  TemplateEditorMode,
} from '../templateEditor/types';
import {
  emptyEditorState,
  getVisibleEditorTabs,
  emptyBladeState,
} from '../templateEditor/types';
import { validateTab, validateAllTabs } from '../utils/templateValidation';

const AUTOSAVE_DELAY_MS = 1500;

export interface UseTemplateEditorStateOptions {
  scope?: 'department' | 'system';
  onAutosave?: (state: TemplateEditorState) => void;
  /**
   * Host-supplied tabs spliced into the built-in list. Defaults to
   * empty; system-web passes a 'visibility' tab. Ids must be unique
   * and must not collide with built-in ids.
   */
  extraTabs?: EditorTabDescriptor[];
  /** Where to splice `extraTabs`. Defaults to 'review'. */
  insertExtraTabsAfter?: TemplateEditorTab;
  /**
   * Skip the 3-card entry gate and land directly in the editor with
   * this mode pre-selected. Used when the URL carries `?mode=shell` or
   * `?mode=bound` (deep-link / refresh persistence), and when Card 3
   * has transitioned the wizard into bound mode with a parked tenant.
   * Omit to show the gate first.
   */
  initialMode?: TemplateEditorMode;
}

export interface UseTemplateEditorStateReturn {
  state: TemplateEditorState;
  setState: Dispatch<SetStateAction<TemplateEditorState>>;
  updateState: (patch: Partial<TemplateEditorState>) => void;

  // Tab navigation — accepts any string id present in the active tab
  // list (built-in or host-supplied).
  goToTab: (tab: string) => void;
  goNext: () => boolean;
  goBack: () => void;
  currentTabIndex: number;
  isLastTab: boolean;
  /** The tab list the hook is currently navigating. */
  tabs: EditorTabDescriptor[];

  // Tab badge helpers
  tabErrors: (tab: TemplateEditorTab) => string[];
  isTabValid: (tab: TemplateEditorTab) => boolean;

  // Blade (FieldMappingDetailBlade)
  openBlade: (index: number | null) => void;
  closeBlade: () => void;
  saveBlade: (mapping: FieldMapping, index: number | null) => void;

  // Validation on final save
  validateForSave: () => boolean;
}

export function useTemplateEditorState(
  options: UseTemplateEditorStateOptions = {},
): UseTemplateEditorStateReturn {
  const {
    scope = 'department',
    onAutosave,
    extraTabs,
    insertExtraTabsAfter = 'review',
    initialMode,
  } = options;

  const [state, setState] = useState<TemplateEditorState>(() => {
    const base = emptyEditorState(scope);
    // Seed mode on the FIRST render so the entry gate never flashes
    // when the host knows up-front which mode to use (deep-link,
    // refresh persistence, or Card 3 transition). `initialMode` is
    // read once at mount — subsequent changes do not re-seed.
    return initialMode ? { ...base, mode: initialMode } : base;
  });

  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Autosave effect — fires 1500ms after any state change
  useEffect(() => {
    if (!state.dirty) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      onAutosave?.(state);
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [state, onAutosave]);

  const updateState = useCallback((patch: Partial<TemplateEditorState>) => {
    setState(prev => ({ ...prev, ...patch, dirty: true }));
  }, []);

  // -------------------------------------------------------------------------
  // Tab list resolution
  //
  // The hook operates off a single source of truth — `tabs` — which is
  // either the caller-supplied override or the mode-filtered built-in
  // list. Navigation arithmetic and `isLastTab` derive from this list so
  // host-injected tabs get full goNext/goBack support for free.
  // -------------------------------------------------------------------------

  // Item 2 (2026-05-08): thread dataMode through to mergeTabs so the
  // geocoding tab is stripped for parcel and lookup imports.
  const tabs = useMemo<EditorTabDescriptor[]>(
    () => mergeTabs(state.mode, extraTabs, insertExtraTabsAfter, state.dataMode),
    [state.mode, state.dataMode, extraTabs, insertExtraTabsAfter],
  );
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  // -------------------------------------------------------------------------
  // Tab navigation
  // -------------------------------------------------------------------------

  const goToTab = useCallback((tab: string) => {
    setState(prev => ({
      ...prev,
      activeTab: tab,
      visitedTabs: new Set([...prev.visitedTabs, tab]),
    }));
  }, []);

  const goNext = useCallback((): boolean => {
    let success = false;
    setState(prev => {
      const list = tabsRef.current;
      const idx = list.findIndex(t => t.id === prev.activeTab);
      // If the active tab isn't in the current list (e.g. the host
      // changed the tab set mid-edit), treat the first tab as the
      // "current" position so navigation still makes forward progress.
      const safeIdx = idx === -1 ? 0 : idx;
      // Validation only applies to built-in tabs; host-injected tabs
      // are treated as always-valid for navigation purposes. Hosts can
      // still gate save via their own validation in `validateForSave`
      // overrides (future extension).
      const isBuiltIn = (id: string): id is TemplateEditorTab =>
        id === 'basics' || id === 'mappings' || id === 'matchKey'
        || id === 'geocoding' || id === 'review';
      if (isBuiltIn(prev.activeTab)) {
        const result = validateTab(prev.activeTab, prev);
        if (!result.valid) {
          return {
            ...prev,
            visitedTabs: new Set([...prev.visitedTabs, prev.activeTab]),
          };
        }
      }
      if (safeIdx >= list.length - 1) {
        // Already on the last tab — mark visited but don't advance.
        return {
          ...prev,
          visitedTabs: new Set([...prev.visitedTabs, prev.activeTab]),
        };
      }
      const next = list[safeIdx + 1]!;
      success = true;
      return {
        ...prev,
        activeTab: next.id,
        visitedTabs: new Set([...prev.visitedTabs, next.id]),
        completedTabs: new Set([...prev.completedTabs, prev.activeTab]),
      };
    });
    return success;
  }, []);

  const goBack = useCallback(() => {
    setState(prev => {
      const list = tabsRef.current;
      const idx = list.findIndex(t => t.id === prev.activeTab);
      if (idx <= 0) return prev;
      const previous = list[idx - 1]!;
      return { ...prev, activeTab: previous.id };
    });
  }, []);

  // -------------------------------------------------------------------------
  // Blade management
  // -------------------------------------------------------------------------

  const openBlade = useCallback((index: number | null) => {
    setState(prev => {
      const draft =
        index !== null ? { ...prev.fieldMappings[index] } : {};
      return {
        ...prev,
        bladeState: { open: true, editingIndex: index, draft } as BladeState,
      };
    });
  }, []);

  const closeBlade = useCallback(() => {
    setState(prev => ({
      ...prev,
      bladeState: emptyBladeState(),
    }));
  }, []);

  const saveBlade = useCallback((mapping: FieldMapping, index: number | null) => {
    setState(prev => {
      const mappings = [...prev.fieldMappings];
      if (index === null) {
        mappings.push(mapping);
      } else {
        mappings[index] = mapping;
      }
      const firstMatchKey = mappings.find(m => m.isMatchKey);
      return {
        ...prev,
        fieldMappings: mappings,
        matchKeyField: prev.matchKeyField || firstMatchKey?.targetField || '',
        bladeState: emptyBladeState(),
        dirty: true,
      };
    });
  }, []);

  // -------------------------------------------------------------------------
  // Validation helpers
  // -------------------------------------------------------------------------

  const tabErrors = useCallback(
    (tab: TemplateEditorTab) => validateTab(tab, state).errors,
    [state],
  );

  const isTabValid = useCallback(
    (tab: TemplateEditorTab) => validateTab(tab, state).valid,
    [state],
  );

  const validateForSave = useCallback((): boolean => {
    const result = validateAllTabs(state);
    if (!result.isValid) {
      setState(prev => ({
        ...prev,
        activeTab: result.errorTabs[0] ?? prev.activeTab,
        visitedTabs: new Set([
          ...prev.visitedTabs,
          ...result.errorTabs,
        ]),
      }));
      return false;
    }
    setState(prev => ({
      ...prev,
      completedTabs: new Set([
        ...prev.completedTabs,
        ...tabsRef.current.map(tab => tab.id),
      ]),
    }));
    return true;
  }, [state]);

  // -------------------------------------------------------------------------
  // Computed
  // -------------------------------------------------------------------------

  const currentTabIndex = Math.max(
    0,
    tabs.findIndex(t => t.id === state.activeTab),
  );
  const isLastTab = currentTabIndex === tabs.length - 1;

  return {
    state,
    setState,
    updateState,
    goToTab,
    goNext,
    goBack,
    currentTabIndex,
    isLastTab,
    tabs,
    tabErrors,
    isTabValid,
    openBlade,
    closeBlade,
    saveBlade,
    validateForSave,
  };
}

// ---------------------------------------------------------------------------
// mergeTabs — splices host-supplied extras into the mode-filtered built-in
// list. Kept as a module-level pure function so it's trivially testable
// and shares the exact implementation used by `UnifiedImportWizard`'s
// prop-level overrides (that wrapper can call through to this helper).
// ---------------------------------------------------------------------------
export function mergeTabs(
  mode: TemplateEditorMode,
  extras: EditorTabDescriptor[] | undefined,
  insertAfter: TemplateEditorTab,
  dataMode?: ImportDataMode | null,
): EditorTabDescriptor[] {
  const builtIn = getVisibleEditorTabs(mode, dataMode);
  if (!extras || extras.length === 0) return builtIn;
  const anchor = builtIn.findIndex(t => t.id === insertAfter);
  if (anchor === -1) return [...builtIn, ...extras];
  return [
    ...builtIn.slice(0, anchor + 1),
    ...extras,
    ...builtIn.slice(anchor + 1),
  ];
}
